<template>
  <div v-if="isDev && isVisible" class="performance-monitor">
    <div class="monitor-header">
      <h4>⚡ Performance Monitor</h4>
      <div class="monitor-actions">
        <button @click="refresh" class="action-btn" title="Refresh">🔄</button>
        <button @click="toggleExpanded" class="action-btn" title="Toggle">
          {{ isExpanded ? '▼' : '▲' }}
        </button>
        <button @click="close" class="action-btn" title="Close">✕</button>
      </div>
    </div>
    
    <div v-if="isExpanded" class="monitor-content">
      <!-- Core Web Vitals -->
      <div class="metric-section">
        <h5>Core Web Vitals</h5>
        <div v-if="webVitals" class="metrics-grid">
          <div class="metric-item">
            <span class="metric-label">FCP</span>
            <span class="metric-value" :class="getVitalClass('fcp', webVitals.firstContentfulPaint)">
              {{ formatTime(webVitals.firstContentfulPaint) }}
            </span>
          </div>
          <div class="metric-item">
            <span class="metric-label">LCP</span>
            <span class="metric-value" :class="getVitalClass('lcp', webVitals.largestContentfulPaint)">
              {{ formatTime(webVitals.largestContentfulPaint) }}
            </span>
          </div>
          <div class="metric-item">
            <span class="metric-label">DOM Ready</span>
            <span class="metric-value">
              {{ formatTime(webVitals.domInteractive) }}
            </span>
          </div>
        </div>
        <div v-else class="loading-text">Loading vitals...</div>
      </div>
      
      <!-- Custom Metrics -->
      <div v-if="customMetrics.length > 0" class="metric-section">
        <h5>Custom Metrics</h5>
        <div class="metrics-list">
          <div v-for="metric in customMetrics" :key="metric.name" class="metric-row">
            <span class="metric-name">{{ metric.name }}</span>
            <span class="metric-value">{{ metric.value.toFixed(2) }}{{ metric.unit }}</span>
          </div>
        </div>
      </div>
      
      <!-- Memory Usage -->
      <div v-if="memoryUsage" class="metric-section">
        <h5>Memory Usage</h5>
        <div class="memory-bar">
          <div 
            class="memory-fill" 
            :style="{ width: memoryUsage.percentage + '%' }"
            :class="getMemoryClass(memoryUsage.percentage)"
          ></div>
        </div>
        <div class="memory-text">
          {{ memoryUsage.usedJSHeapSize }} MB / {{ memoryUsage.totalJSHeapSize }} MB
          ({{ memoryUsage.percentage }}%)
        </div>
      </div>
      
      <!-- Actions -->
      <div class="monitor-footer">
        <button @click="clearMetrics" class="footer-btn">Clear Metrics</button>
        <button @click="logReport" class="footer-btn">Log Report</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { getCoreWebVitals, getMetrics, clearMetrics as clearPerformanceMetrics, logPerformanceReport } from '@/utils/performance'
import { useMemoryMonitoring } from '@/composables/usePerformance'

const isDev = import.meta.env.DEV
const isVisible = ref(true)
const isExpanded = ref(true)
const webVitals = ref(null)
const metrics = ref({ custom: new Map() })

const { memoryUsage, isSupported: memorySupported, updateMemoryUsage } = useMemoryMonitoring()

const customMetrics = computed(() => {
  const customMap = metrics.value.custom
  if (!customMap || customMap.size === 0) return []
  
  return Array.from(customMap.entries()).map(([name, data]) => ({
    name,
    value: data.value,
    unit: data.unit
  }))
})

async function loadWebVitals() {
  webVitals.value = await getCoreWebVitals()
}

function refresh() {
  metrics.value = getMetrics()
  loadWebVitals()
  if (memorySupported.value) {
    updateMemoryUsage()
  }
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

function close() {
  isVisible.value = false
}

function clearMetrics() {
  clearPerformanceMetrics()
  metrics.value = { custom: new Map() }
  console.log('✅ Performance metrics cleared')
}

function logReport() {
  logPerformanceReport()
}

function formatTime(ms) {
  if (!ms) return 'N/A'
  return ms < 1000 ? `${ms.toFixed(0)}ms` : `${(ms / 1000).toFixed(2)}s`
}

function getVitalClass(type, value) {
  if (!value) return ''
  
  const thresholds = {
    fcp: { good: 1800, poor: 3000 },
    lcp: { good: 2500, poor: 4000 }
  }
  
  const threshold = thresholds[type]
  if (!threshold) return ''
  
  if (value <= threshold.good) return 'good'
  if (value <= threshold.poor) return 'needs-improvement'
  return 'poor'
}

function getMemoryClass(percentage) {
  if (percentage < 50) return 'good'
  if (percentage < 75) return 'needs-improvement'
  return 'poor'
}

onMounted(() => {
  refresh()
  
  // Auto-refresh every 5 seconds
  const interval = setInterval(() => {
    if (isVisible.value && isExpanded.value) {
      refresh()
    }
  }, 5000)
  
  // Cleanup
  return () => clearInterval(interval)
})
</script>

<style scoped>
.performance-monitor {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 12px;
  z-index: 9999;
  backdrop-filter: blur(10px);
}

.monitor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.monitor-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.monitor-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  transition: background 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.monitor-content {
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
}

.metric-section {
  margin-bottom: 16px;
}

.metric-section:last-child {
  margin-bottom: 0;
}

.metric-section h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.metric-item {
  display: flex;
  flex-direction: column;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.metric-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 14px;
  font-weight: 600;
}

.metric-value.good {
  color: #4caf50;
}

.metric-value.needs-improvement {
  color: #ff9800;
}

.metric-value.poor {
  color: #f44336;
}

.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.metric-name {
  font-size: 11px;
  color: #ccc;
}

.memory-bar {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.memory-fill {
  height: 100%;
  transition: width 0.3s, background-color 0.3s;
}

.memory-fill.good {
  background: #4caf50;
}

.memory-fill.needs-improvement {
  background: #ff9800;
}

.memory-fill.poor {
  background: #f44336;
}

.memory-text {
  font-size: 11px;
  color: #ccc;
  text-align: center;
}

.loading-text {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.monitor-footer {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-btn {
  flex: 1;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: background 0.2s;
}

.footer-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Scrollbar styling */
.monitor-content::-webkit-scrollbar {
  width: 6px;
}

.monitor-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.monitor-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.monitor-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
